import { useState } from 'react';
import { Download } from 'lucide-react';
import { LATEST } from './exported-downloads';

declare module './paige.global' {
  interface PaigeWorkbenchBridge {
    /** Saves the job's exported documents as one zip; how many went in (exported-downloads.ts). */
    downloadExported?: (zipName: string) => Promise<number>;
  }
}

type State = 'ready' | 'getting' | 'none' | 'problem' | { saved: number };

/** What the button's line says after a press. */
export function downloadWords(state: State): string | null {
  if (state === 'none') return 'Nothing is exported yet.';
  if (state === 'problem') return 'The download did not finish. Try again.';
  if (typeof state === 'object')
    return state.saved >= LATEST
      ? `Saved the latest ${LATEST.toLocaleString()}.`
      : `Saved ${state.saved === 1 ? '1 document' : `${state.saved.toLocaleString()} documents`}.`;
  return null;
}

/**
 * PAIGE: the Download destination's own button in "Send results to": the job's exported documents, each a
 * searchable PDF with what was read beside it, as one zip. Without a Paige job channel it renders nothing.
 */
export function DownloadExported() {
  const [state, setState] = useState<State>('ready');
  const save = window.paigeWorkbench?.downloadExported;
  if (!save) return null;
  const press = async () => {
    if (state === 'getting') return;
    setState('getting');
    try {
      const saved = await save('Exported documents.zip');
      setState(saved === 0 ? 'none' : { saved });
    } catch {
      setState('problem');
    }
  };
  const words = downloadWords(state);
  return (
    <span className="paige-destination-actions">
      {words && <small role="status">{words}</small>}
      <button
        type="button"
        className="quiet-button small"
        disabled={state === 'getting'}
        onClick={() => void press()}
      >
        <Download size={12} aria-hidden="true" />
        {state === 'getting' ? 'Getting them ready…' : 'Download'}
      </button>
    </span>
  );
}
